import { Box, Flex, Input, Text, useToast } from '@chakra-ui/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import {
  CreditAgreement,
  typeguards as finleyTypeguards,
} from '@finley-technologies/finley-types';
import { validate as reportingValidate } from '@finley-technologies/reporting-utilities';

import { ReportTemplateContainer } from './template/ReportTemplateContainer';
import { ReportXlsxOutputSheetsContainer } from './xlsxOutputSheets/ReportXlsxOutputSheetsContainer';
import {
  EditIconButton,
  SaveIconButton,
  XIconButton,
} from 'packages/common-frontend/components/IconButton';
import { CreditAgreementFullArtifactWipConfigType } from 'packages/creditAgreements/ui/types';

type ReportingOutputXlsxConfigProps = {
  creditAgreementId: string;
  reportName: string;
  outputConfig: CreditAgreement.Config.Reporting.Outputs.Xlsx.Config;
  reportConfigTypes: CreditAgreement.DomainEntity.ReportConfigType[];
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  basePropertyKey: string;
  queryResults: CreditAgreement.Config.QueryResult[];
};
export const ReportingOutputXlsxConfig = ({
  creditAgreementId,
  reportName,
  outputConfig,
  reportConfigTypes,
  wipConfig,
  basePropertyKey,
  queryResults,
}: ReportingOutputXlsxConfigProps) => {
  const { name: wbOutputName, sheets, template } = outputConfig;
  const sheetPropertyKey = `${basePropertyKey}.sheets`;

  const toast = useToast();

  // *** WORKBOOK NAME ***
  const [workbookName, setWorkbookName] = useState<string>(wbOutputName);
  const [isWorkbookNameEditOpen, setIsWorkbookNameEditOpen] = useState<boolean>(
    false,
  );

  const updateWorkbookName = (): void => {
    if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
      return;
    }

    const wipOutputConfig = getIn(wipConfig.formProps.values, basePropertyKey);
    finleyAssert(
      finleyTypeguards.creditAgreement.isReportingOutputsXlsxConfig(
        wipOutputConfig,
      ),
      `Retrieved non-Output Config structure for [key:${basePropertyKey}]`,
    );

    const outputConfigCopy = _.cloneDeep(wipOutputConfig);
    outputConfigCopy.name = workbookName;
    wipConfig.formProps.setFieldValue(basePropertyKey, outputConfigCopy);
  };

  // *** Tracking which "sheet" to focus on ***
  const [targetSheetIdx, setTargetSheetIdx] = useState<number>(0);

  return (
    <Box id="reporting-output-config-container">
      <Flex alignItems="center" mb="3">
        <Text as="h2" fontWeight="bold" fontSize="xl" mr="2">
          Output Config
        </Text>

        {wipConfig?.isMutateAllowed(sheetPropertyKey) && (
          <>
            <SaveIconButton
              ariaLabel="save output config"
              label="Save Output Config"
              onClick={() => {
                const newOutputConfig = getIn(
                  wipConfig.formProps.values,
                  basePropertyKey,
                );
                finleyAssert(
                  finleyTypeguards.creditAgreement.isReportingOutputsXlsxConfig(
                    newOutputConfig,
                  ),
                  `Retrieved non-Output XLSX Config structure for [key:${sheetPropertyKey}]`,
                );

                const outputErrors = reportingValidate.output.validateReportingOutputXlsxConfig(
                  { config: newOutputConfig },
                );
                if (outputErrors.length > 0) {
                  toast({
                    title: 'Saving Output Config Error',
                    description: outputErrors.join(', '),
                    status: 'error',
                    position: 'top',
                    duration: 8000,
                    isClosable: true,
                  });
                  return;
                }

                wipConfig.saveAndUpdateConfigCreditAgreementWip({
                  wipConfig,
                  propertyKey: basePropertyKey,
                  updatedProperty: newOutputConfig,
                });
              }}
            />
          </>
        )}
      </Flex>

      {wipConfig?.isMutateAllowed(sheetPropertyKey) && (
        <Text fontStyle="italic" fontSize="md" mb="6">
          Note that this &quot;save&quot; only saves the Output config portion
        </Text>
      )}

      {/* *** Manage Workbook Name *** */}
      <Flex
        alignItems="center"
        border="1px"
        borderColor="gray.700"
        p="3"
        mb="4"
      >
        <Text width="56" mr="3">
          Generated Workbook Name:
        </Text>

        {isWorkbookNameEditOpen &&
        wipConfig?.isMutateAllowed(basePropertyKey) ? (
          <>
            <Input
              mr="2"
              type="text"
              width="96"
              value={workbookName}
              onChange={(e) => setWorkbookName(e.target.value)}
            />
            <SaveIconButton
              ariaLabel="save name"
              label="Save Name"
              onClick={() => {
                updateWorkbookName();
                setIsWorkbookNameEditOpen(false);
              }}
              isDisabled={!workbookName}
            />
            <XIconButton
              ariaLabel="close edit"
              label="Close Edit"
              onClick={() => setIsWorkbookNameEditOpen(false)}
            />
          </>
        ) : (
          <>
            <Text width="96" px="4">
              {wbOutputName}
            </Text>
            {wipConfig?.isMutateAllowed(basePropertyKey) && (
              <EditIconButton
                ariaLabel="edit name"
                label="Edit Name"
                onClick={() => setIsWorkbookNameEditOpen(true)}
                ml="2"
              />
            )}
          </>
        )}
      </Flex>

      <ReportTemplateContainer
        reportConfigType={reportConfigTypes[0]}
        templateConfig={template}
        templatePropertyKey={`${basePropertyKey}.template`}
        wipConfig={wipConfig}
        fileType="xlsx"
        reportName={reportName}
        creditAgreementId={creditAgreementId}
      />

      <ReportXlsxOutputSheetsContainer
        wipConfig={wipConfig}
        sheets={sheets}
        sheetsPropertyKey={`${basePropertyKey}.sheets`}
        targetSheetIdx={targetSheetIdx}
        setTargetSheetIdx={setTargetSheetIdx}
        queryResults={queryResults}
      />
    </Box>
  );
};
